import { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, ArrowUp, ArrowDown, X, ImagePlus, Home } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api, getApiErrorMessage } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import type { ContentFormData, UploadedAsset } from '..';

interface UploadAssetsProps {
  formData: ContentFormData;
  updateFormData: (data: Partial<ContentFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

const MIN_SECS = 5;
const MAX_SECS = 30;
const DEFAULT_SECS = 6;

// Upload-mode style presets. 'none' keeps the clean default slideshow;
// 'property-tour' switches on estate-agent narration + AI pan/zoom + dissolves.
const UPLOAD_PRESETS = [
  {
    id: 'none',
    name: 'Standard',
    desc: 'Clean slideshow with a friendly voiceover.',
    icon: ImagePlus,
  },
  {
    id: 'property-tour',
    name: 'Property Tour',
    desc: 'Estate-agent voiceover, pan & zoom, smooth transitions.',
    icon: Home,
  },
] as const;

export function UploadAssets({ formData, updateFormData, onValidationChange }: UploadAssetsProps) {
  const [assets, setAssets] = useState<UploadedAsset[]>(formData.uploadedAssets || []);
  const [topic, setTopic] = useState(formData.topic || '');
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Frame previews by PLATFORM — the same signal the backend uses to pick
  // output dimensions and the storyboard uses for its preview — so the upload
  // preview always matches the final video (landscape platforms => 16:9).
  const PORTRAIT_PLATFORMS = ['tiktok', 'instagram', 'shorts'];
  const isPortrait = PORTRAIT_PLATFORMS.includes((formData.platform || '').toLowerCase());

  useEffect(() => {
    onValidationChange(assets.length > 0 && topic.trim().length > 0);
    updateFormData({ uploadedAssets: assets });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, topic]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      // Upload + AI-describe all picked images in parallel (each describe is a
      // vision call, so serial would be slow for a batch).
      const results = await Promise.all(
        Array.from(files).map(async (file): Promise<UploadedAsset | null> => {
          const body = new FormData();
          body.append('file', file);
          try {
            const res = await api.post('/content/upload-asset', body);
            // Default the framing: crop-to-fill when the image orientation
            // matches the platform, fit + blurred-bg when it doesn't.
            const dims = await new Promise<{ w: number; h: number }>(resolve => {
              const probe = new Image();
              probe.onload = () => resolve({ w: probe.naturalWidth, h: probe.naturalHeight });
              probe.onerror = () => resolve({ w: 1, h: 1 });
              probe.src = res.data.url;
            });
            const imgPortrait = dims.h > dims.w;
            return {
              fileKey: res.data.file_key,
              url: res.data.url,
              duration: DEFAULT_SECS,
              description: res.data.description || '',
              script: '',
              fit: imgPortrait === isPortrait ? 'cover' : 'contain',
            };
          } catch (error) {
            toast({
              title: `Couldn't upload ${file.name}`,
              description: getApiErrorMessage(error, 'Upload failed. Try a JPG, PNG, or WebP under 15MB.'),
              variant: 'destructive',
            });
            return null;
          }
        })
      );
      const uploaded = results.filter((r): r is UploadedAsset => r !== null);
      if (uploaded.length) setAssets(prev => [...prev, ...uploaded]);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const update = (i: number, patch: Partial<UploadedAsset>) =>
    setAssets(prev => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));

  const move = (i: number, dir: -1 | 1) =>
    setAssets(prev => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const remove = (i: number) => setAssets(prev => prev.filter((_, idx) => idx !== i));

  const totalSecs = assets.reduce((sum, a) => sum + a.duration, 0);

  return (
    <div className={`${isPortrait ? 'max-w-[680px]' : 'max-w-[960px]'} mx-auto space-y-6`}>
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-semibold mb-1">Upload your media</h2>
        <p className="text-sm text-muted-foreground">
          Add images in order, set how long each shows, and write the voiceover line for each.
          Framed <span className="font-medium">{isPortrait ? '9:16 vertical' : '16:9 landscape'}</span> to match your platform.
        </p>
      </div>

      {/* Topic — used to title the video and auto-narrate any blank scenes */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">What's this video about?</Label>
        <Input
          value={topic}
          onChange={e => {
            setTopic(e.target.value);
            updateFormData({ topic: e.target.value });
          }}
          placeholder="e.g. A cozy 2-bed flat in Shoreditch"
        />
      </div>

      {/* Style preset — drives narration voice + motion in the final video */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Style</Label>
        <div className="grid grid-cols-2 gap-2">
          {UPLOAD_PRESETS.map(preset => {
            const selected = (formData.presetId || 'none') === preset.id;
            const Icon = preset.icon;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => updateFormData({ presetId: preset.id })}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  selected
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/20 hover:border-primary/40'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="text-sm font-medium">{preset.name}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{preset.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Dropzone / picker */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full rounded-xl border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 transition-colors py-8 flex flex-col items-center gap-2 text-muted-foreground"
      >
        {uploading ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : (
          <Upload className="h-6 w-6" />
        )}
        <span className="text-sm font-medium">
          {uploading ? 'Uploading…' : 'Click to add images'}
        </span>
        <span className="text-xs">JPG, PNG, or WebP · up to 15MB each</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
      />

      {assets.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{assets.length} scene{assets.length === 1 ? '' : 's'}</span>
          <span>~{totalSecs}s total</span>
        </div>
      )}

      <div className="space-y-3">
        {assets.map((asset, i) => (
          <Card key={asset.fileKey} className="p-3 flex gap-3">
            <div
              className={`relative shrink-0 overflow-hidden rounded-lg bg-black ${
                isPortrait
                  ? 'w-[72px] aspect-[9/16]'
                  : 'w-32 sm:w-44 md:w-52 aspect-video'
              }`}
            >
              {asset.fit === 'contain' && (
                <img
                  src={asset.url}
                  aria-hidden
                  className="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-md"
                />
              )}
              <img
                src={asset.url}
                alt={`Scene ${i + 1}`}
                className={`relative h-full w-full ${
                  asset.fit === 'contain' ? 'object-contain' : 'object-cover'
                }`}
              />
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Scene {i + 1}</span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)}>
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === assets.length - 1} onClick={() => move(i, 1)}>
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => remove(i)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Input
                value={asset.description}
                onChange={e => update(i, { description: e.target.value })}
                placeholder="What's in this image (auto-described — fix if wrong)"
                className="h-8 text-xs"
              />
              <Textarea
                value={asset.script}
                onChange={e => update(i, { script: e.target.value })}
                placeholder="Voiceover… (blank = auto from topic + image description)"
                className="min-h-[52px] text-sm resize-none"
              />
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Duration</Label>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={asset.duration <= MIN_SECS}
                      onClick={() => update(i, { duration: Math.max(MIN_SECS, asset.duration - 1) })}
                    >
                      −
                    </Button>
                    <span className="w-10 text-center text-sm tabular-nums">{asset.duration}s</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={asset.duration >= MAX_SECS}
                      onClick={() => update(i, { duration: Math.min(MAX_SECS, asset.duration + 1) })}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Frame</Label>
                  <div className="flex rounded-md border p-0.5">
                    {(['cover', 'contain'] as const).map(mode => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => update(i, { fit: mode })}
                        className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                          asset.fit === mode
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {mode === 'cover' ? 'Fill' : 'Fit'}
                      </button>
                    ))}
                  </div>
                  {asset.fit === 'contain' && formData.presetId === 'property-tour' && (
                    <span className="text-[11px] text-muted-foreground">
                      shows full photo · no pan/zoom
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {assets.length === 0 && !uploading && (
        <div className="text-center text-sm text-muted-foreground flex flex-col items-center gap-2 py-4">
          <ImagePlus className="h-8 w-8 opacity-40" />
          Add at least one image to continue.
        </div>
      )}
    </div>
  );
}
